// packages/editor/src/slot-grammar/serialize-slot.ts
//
// Emits the canonical platform form for slot directives:
//   :::slot[name]{schema=X source=Y reviewer=Z slash=a,b,c}:::
// Single-line three-colon. `normalizeAgentMarkdown` rewrites this to
// single-colon leaf form (`:slot[name]{attrs}`) before the parser sees
// it — the slot regex was added in the same commit that introduced
// this serializer. For E2 round-trip we test the parse path explicitly.

import type { SlotConfig } from './slot-types';

const DEFAULTS = {
  schema: 'Markdown',
  source: 'human',
  reviewer: 'none',
} as const;

function attrPair(key: string, value: string): string {
  // Quote values containing spaces or attribute-significant chars.
  if (/[\s"'={}]/.test(value)) {
    return `${key}="${value.replace(/"/g, '\\"')}"`;
  }
  return `${key}=${value}`;
}

export function serializeSlot(cfg: SlotConfig): string {
  const attrs: string[] = [];

  if (cfg.schema !== DEFAULTS.schema) attrs.push(attrPair('schema', cfg.schema));
  if (cfg.source !== DEFAULTS.source) attrs.push(attrPair('source', cfg.source));
  if (cfg.reviewer !== DEFAULTS.reviewer) attrs.push(attrPair('reviewer', cfg.reviewer));
  if (cfg.trigger) attrs.push(attrPair('trigger', cfg.trigger));
  if (cfg.slash.length > 0) attrs.push(attrPair('slash', cfg.slash.join(',')));
  if (cfg.maxWords !== undefined) attrs.push(`maxWords=${cfg.maxWords}`);
  if (cfg.maxBytes !== undefined) attrs.push(`maxBytes=${cfg.maxBytes}`);
  if (cfg.maxItems !== undefined) attrs.push(`maxItems=${cfg.maxItems}`);
  if (cfg.default) attrs.push(attrPair('default', cfg.default));

  const attrPart = attrs.length > 0 ? `{${attrs.join(' ')}}` : '';
  return `:::slot[${cfg.name}]${attrPart}:::`;
}
